/* Logo Enrichment — V1 operator console.

   Minimal surface over the existing async enrichment endpoints:
     - POST /api/data/admin/logos/enrich  (returns 202 + run_id)
     - GET  /api/data/admin/logos/enrich/{run_id}  (poll status)

   Sections (top → bottom):
     1. Start Enrichment panel  (mode toggle + batch size + start button)
     2. Current/Latest Run summary  (status + counter tiles)
     3. Affected Companies table  (sample_outcomes, deep-link to detail)
     4. Download JSON  (raw report blob)

   No past-runs list, no rollback, no real-time row progress. The poll
   loop runs at 2s while status ∈ {queued, running} and stops cleanly
   on completed/failed.

   Auth: backend gates POST/GET with require_primary_super_admin().
   Frontend gate via user.is_primary_admin so non-primary admins never
   reach the page (App.js route-level + sidebar visibility).
*/

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Play, ArrowSquareOut, DownloadSimple, CheckCircle,
    XCircle, Clock, CircleNotch, ImageBroken, X, ListBullets,
    Info, ClipboardText,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DestructiveConfirmDialog from "../../components/admin/DestructiveConfirmDialog";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = new Set(["completed", "failed"]);
const DEFAULT_BATCH_SIZE = 50;

// Concurrency is locked per mode per the support brief — live runs at
// concurrency=2 to stay polite to upstream icon services, dry-runs at 4
// so operators can iterate faster.
const CONCURRENCY_BY_MODE = { dry_run: 4, live: 2 };

function StatusBadge({ status }) {
    const map = {
        queued: {
            Icon: Clock, cls: "bg-slate-100 text-slate-700",
            label: "Queued",
        },
        running: {
            Icon: CircleNotch, cls: "bg-blue-100 text-blue-700 animate-pulse",
            label: "Running", spin: true,
        },
        completed: {
            Icon: CheckCircle, cls: "bg-emerald-100 text-emerald-700",
            label: "Completed",
        },
        failed: {
            Icon: XCircle, cls: "bg-rose-100 text-rose-700",
            label: "Failed",
        },
    };
    const entry = map[status] || map.queued;
    const Icon = entry.Icon;
    return (
        <span
            data-testid={`run-status-${status}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${entry.cls}`}
        >
            <Icon size={12} weight="fill" className={entry.spin ? "animate-spin" : ""} />
            {entry.label}
        </span>
    );
}

function Tile({ label, value, accent, testId }) {
    return (
        <div data-testid={testId} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="reedos-eyebrow text-slate-500">{label}</div>
            <div className={`text-2xl font-display font-bold tabular-nums mt-1.5 ${accent || "text-slate-900"}`}>
                {value.toLocaleString()}
            </div>
        </div>
    );
}

function ConfidenceChip({ value }) {
    const map = {
        high: "bg-emerald-100 text-emerald-700",
        medium: "bg-blue-100 text-blue-700",
        low: "bg-amber-100 text-amber-700",
        "domain-only": "bg-slate-200 text-slate-700",
        none: "bg-slate-100 text-slate-500",
    };
    return (
        <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${map[value] || map.none}`}>
            {value || "—"}
        </span>
    );
}

// V2.0 confidence-scoring cell — displays the composite ``confidence_score``
// (0.000-1.000) with a colour tone reflecting the threshold gate it lands in:
//   write  (≥ 0.55) → emerald
//   downgrade (≥ 0.30) → amber
//   reject (< 0.30) → rose
// Hovering the chip reveals the per-component breakdown so operators can
// audit WHY a candidate landed where it did without leaving the row. The
// five sub-scores match the V2.0 design memo:
//   tc  = token_coverage      (weight 0.20)
//   bmr = body_match_ratio    (weight 0.35)
//   sp  = stem_specificity    (weight 0.20)
//   sa  = source_authority    (weight 0.20)
//   cp  = composite_penalty   (weight 0.05)
const SCORE_WEIGHTS = { tc: 0.20, bmr: 0.35, sp: 0.20, sa: 0.20, cp: 0.05 };
const SCORE_LABELS = {
    tc: "token_coverage",
    bmr: "body_match_ratio",
    sp: "stem_specificity",
    sa: "source_authority",
    cp: "composite_penalty",
};
function ScoreCell({ score, breakdown, companyId }) {
    if (score === null || score === undefined) {
        return <span className="text-slate-300 text-xs">—</span>;
    }
    let tone;
    let gateLabel;
    if (score >= 0.55) { tone = "bg-emerald-100 text-emerald-700"; gateLabel = "write"; }
    else if (score >= 0.30) { tone = "bg-amber-100 text-amber-700"; gateLabel = "downgrade"; }
    else { tone = "bg-rose-100 text-rose-700"; gateLabel = "reject"; }
    return (
        <div className="relative group inline-block" data-testid={`score-cell-${companyId}`}>
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] tabular-nums ${tone}`}
                title={`V2.0 gate: ${gateLabel}`}
            >
                {score.toFixed(3)}
            </span>
            {breakdown && Object.keys(breakdown).length > 0 && (
                <div
                    className="absolute z-30 top-full mt-1 right-0 hidden group-hover:block w-64 rounded-md border border-slate-200 bg-white shadow-lg p-2.5"
                    data-testid={`score-breakdown-${companyId}`}
                >
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
                        V2.0 score breakdown
                    </div>
                    <table className="w-full text-[11px]">
                        <tbody>
                            {["tc", "bmr", "sp", "sa", "cp"].map((k) => {
                                const v = breakdown[k];
                                if (v === null || v === undefined) return null;
                                const w = SCORE_WEIGHTS[k];
                                return (
                                    <tr key={k} className="border-b border-slate-100 last:border-0">
                                        <td className="py-1 pr-2 text-slate-600">{SCORE_LABELS[k]}</td>
                                        <td className="py-1 px-1 text-slate-400 font-mono tabular-nums">×{w.toFixed(2)}</td>
                                        <td className="py-1 pl-1 text-right font-mono tabular-nums text-slate-900">{v.toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                            <tr className="bg-slate-50">
                                <td className="py-1 pr-2 font-semibold text-slate-700">composite</td>
                                <td></td>
                                <td className="py-1 pl-1 text-right font-mono tabular-nums font-semibold text-slate-900">{score.toFixed(3)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="text-[10px] text-slate-400 mt-1.5">
                        Thresholds: write ≥ 0.55 · downgrade ≥ 0.30 · reject &lt; 0.30
                    </div>
                </div>
            )}
        </div>
    );
}

function OutcomeChip({ row }) {
    if (row.wrote) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">wrote</span>;
    }
    if (row.would_write) {
        // Dry-run sibling of "wrote" — the commit gate would pass. Distinct
        // styling so the operator never confuses an actual write with a
        // prospective one.
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">would write</span>;
    }
    if (row.suppress_commit || row.collision_with) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">collision</span>;
    }
    // 2026-02 — third-party portal suppression is an emphatic state
    // (the row tried to leak a publisher/portal logo). Renders as a
    // distinct rose chip ahead of the generic "skipped" so the
    // operator can spot it at a glance.
    if (row.third_party_category) {
        return (
            <span
                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200"
                title={`Third-party host: ${row.third_party_host || "?"}`}
            >
                3rd-party
            </span>
        );
    }
    if (row.skipped_reason) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title={row.skipped_reason}>skipped</span>;
    }
    if (row.discovered_domain) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">domain-only</span>;
    }
    return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">unresolved</span>;
}

// Single source of truth for filter-chip categorisation. Mirrors the
// OutcomeChip priority order so chip counts always equal what the table
// would show after filtering. ``unresolved`` is folded into ``skipped``
// because every unresolved row carries a populated ``skipped_reason``
// (``domain-discovery-failed``) from the enrichment engine.
function outcomeCategory(r) {
    if (r.wrote || r.would_write) return "would_write";
    if (r.suppress_commit || r.collision_with) return "collision";
    if (r.third_party_category) return "third_party";
    if (r.skipped_reason) return "skipped";
    if (r.discovered_domain) return "domain_only";
    return "skipped";
}

// Logo candidate preview. The URL is the LIVE external icon source
// (google-s2 / website apple-touch-icon / svg favicon / ddg favicon)
// that the engine resolved during dry-run. We render it directly via
// ``<img>`` so the operator sees exactly the bytes the live commit would
// fetch. ``no-referrer`` keeps origin-policy-restricted sources from
// 403-ing on the preview. Broken-image fallback ensures the row stays
// visible even if the candidate URL has gone stale between the dry-run
// and review.
function LogoCandidatePreview({ url }) {
    const [errored, setErrored] = useState(false);
    if (!url || errored) {
        return (
            <div
                data-testid="logo-candidate-empty"
                className="h-8 w-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center"
            >
                <ImageBroken size={12} className="text-slate-400" />
            </div>
        );
    }
    return (
        <img
            src={url}
            alt="logo candidate"
            data-testid="logo-candidate-preview"
            referrerPolicy="no-referrer"
            className="h-8 w-8 rounded object-contain bg-white border border-slate-200 p-0.5"
            onError={() => setErrored(true)}
        />
    );
}

function FilterChip({ active, label, count, color, onClick, testId }) {
    const palette = active
        ? (color || "bg-slate-900 text-white border-slate-900")
        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50";
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors ${palette}`}
        >
            <span>{label}</span>
            <span className={`tabular-nums font-mono text-[11px] ${active ? "opacity-80" : "text-slate-400"}`}>{count}</span>
        </button>
    );
}

export default function LogoEnrichment() {
    const location = useLocation();
    const navigate = useNavigate();
    // Capture incoming re-cohort once at mount via a lazy ref so the
    // prefill state is consumed exactly once and a browser refresh
    // (which carries the state forward in history) doesn't re-trigger.
    const initialPrefill = location.state?.prefillCohort || null;
    const [mode, setMode] = useState(initialPrefill?.suggested_mode || "dry_run");
    const initialBatch = initialPrefill?.company_ids?.length
        ? Math.max(1, Math.min(2000, initialPrefill.company_ids.length))
        : DEFAULT_BATCH_SIZE;
    const [batchSize, setBatchSize] = useState(initialBatch);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [starting, setStarting] = useState(false);
    const [run, setRun] = useState(null);  // latest LogoEnrichReport
    const [eta, setEta] = useState(null);  // seconds, from 202 response
    const pollRef = useRef(null);

    // Logo Enrichment v2 UX polish (iter 86) — page-load-only fetch of
    // the global Review Queue stats so we can show a "Pending Reviews"
    // shortcut at the top of the Launcher. Per operator directive the
    // count refreshes ONLY on page load (no polling).
    const [reviewStats, setReviewStats] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/data/admin/logos/review-queue-stats");
                if (cancelled) return;
                setReviewStats(data);
            } catch {
                // Stats are a nice-to-have; ignore failures silently.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Iter 39 Phase 4 — operator navigates here from Run Detail with a
    // pre-built company-id list. Suggested mode is always ``dry_run``
    // (operator directive 2026-02-10).
    const [prefillCohort, setPrefillCohort] = useState(initialPrefill);
    // Strip the state out of history exactly once after mount so a
    // browser-refresh on this page doesn't re-replay the prefill.
    useEffect(() => {
        if (location.state?.prefillCohort) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);
    const clearPrefill = () => setPrefillCohort(null);

    // Stop the poll loop on unmount so we don't leak timers.
    useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

    const pollRun = async (runId) => {
        try {
            const { data } = await api.get(`/data/admin/logos/enrich/${runId}`);
            setRun(data);
            if (!TERMINAL_STATUSES.has(data.status)) {
                pollRef.current = setTimeout(() => pollRun(runId), POLL_INTERVAL_MS);
            } else if (data.status === "completed") {
                toast.success(`Enrichment ${runId} completed — ${data.rows_wrote_mongo} written.`);
            } else if (data.status === "failed") {
                toast.error(`Enrichment ${runId} failed: ${data.error_message || "unknown error"}`);
            }
        } catch (err) {
            // Transient poll failure — try once more after the normal interval.
            // If the run id genuinely doesn't exist (404), the toast surfaces and
            // we stop the loop.
            const status = err?.response?.status;
            if (status === 404) {
                toast.error(formatApiError(err, "Run not found"));
                return;
            }
            pollRef.current = setTimeout(() => pollRun(runId), POLL_INTERVAL_MS);
        }
    };

    const startRun = async () => {
        setStarting(true);
        setConfirmOpen(false);
        try {
            const concurrency = CONCURRENCY_BY_MODE[mode] || 2;
            // Iter 39 — if a re-cohort was pre-filled, ride the
            // targeted-company_ids body path so we run only the
            // operator-selected subset rather than the global
            // placeholder cohort.
            const body = prefillCohort?.company_ids?.length
                ? { company_ids: prefillCohort.company_ids }
                : null;
            const { data } = await api.post(
                "/data/admin/logos/enrich",
                body,
                { params: { mode, max_rows: batchSize, concurrency, reset: false } },
            );
            setEta(data.estimated_duration_seconds);
            // Seed the run record with the 202 fields so the UI updates
            // immediately (queued state) before the first poll lands.
            setRun({
                run_id: data.run_id,
                mode: data.mode,
                status: data.status,
                started_at: data.started_at,
                cohort_total: data.cohort_total,
            });
            toast.success(`Run ${data.run_id} queued — ETA ~${data.estimated_duration_seconds}s.`);
            // Consume the prefill — a single cohort is one-shot.
            setPrefillCohort(null);
            pollRun(data.run_id);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to start enrichment"));
        } finally {
            setStarting(false);
        }
    };

    const onClickStart = () => {
        if (batchSize < 1 || batchSize > 2000) {
            toast.error("Batch size must be between 1 and 2000.");
            return;
        }
        if (mode === "live") {
            setConfirmOpen(true);
        } else {
            startRun();
        }
    };

    const downloadJson = () => {
        if (!run) return;
        const blob = new Blob([JSON.stringify(run, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `enrichment-${run.run_id}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const isTerminal = run && TERMINAL_STATUSES.has(run.status);
    const isLive = run?.mode === "live";

    // Filter-chip state for the "Affected companies" table. Defaults to
    // ``all`` so the operator sees everything until they explicitly narrow
    // down. Counts and the filtered slice are memoised against the run's
    // sample_outcomes so the table never re-iterates on unrelated re-renders.
    const [filter, setFilter] = useState("all");
    const { counts, filteredOutcomes } = useMemo(() => {
        const outcomes = run?.sample_outcomes || [];
        const c = { all: outcomes.length, would_write: 0, domain_only: 0, collision: 0, third_party: 0, skipped: 0 };
        for (const r of outcomes) {
            const cat = outcomeCategory(r);
            c[cat] = (c[cat] || 0) + 1;
        }
        const filtered = filter === "all"
            ? outcomes
            : outcomes.filter((r) => outcomeCategory(r) === filter);
        return { counts: c, filteredOutcomes: filtered };
    }, [run, filter]);

    return (
        <div data-testid="logo-enrichment-page">
            <PageHeader
                eyebrow="Data · Company Repository"
                title="Logo Enrichment"
                description="Discover corporate domains and logo candidates for placeholder companies. Results are queued for human verification in the Review tab before being added to the Company Repository."
                actions={(
                    <Link to="/data/logo-enrichment/runs">
                        <Button variant="outline" size="sm" className="gap-2" data-testid="run-history-link">
                            <ListBullets size={14} />
                            Run history
                        </Button>
                    </Link>
                )}
            />

            <div className="px-6 sm:px-8 py-6 space-y-6 max-w-6xl">

                {/* Logo Enrichment v2 UX polish (iter 86) — Review Queue
                    informational banner. Replaces the previous red
                    technical warning. Tone: blue / informational. */}
                <div
                    className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                    data-testid="review-queue-info-banner"
                >
                    <Info size={20} weight="fill" className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-blue-900">
                            Review Queue enabled
                        </div>
                        <div className="text-xs text-blue-800 mt-1 leading-relaxed">
                            Logo enrichment results now require human verification before being added to the Company Repository.
                            Run enrichment normally and approve results from the Review tab.
                        </div>
                    </div>
                </div>

                {/* Logo Enrichment v2 UX polish (iter 86) — Pending
                    Reviews shortcut. Page-load fetch only, no polling. */}
                {reviewStats && (reviewStats.pending_review || 0) > 0 && (
                    <div
                        className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
                        data-testid="pending-reviews-shortcut"
                    >
                        <ClipboardText size={20} weight="fill" className="text-amber-700 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-amber-900">
                                <span data-testid="pending-reviews-count">{reviewStats.pending_review.toLocaleString()}</span> logo
                                {reviewStats.pending_review === 1 ? "" : "s"} waiting for verification
                            </div>
                            <div className="text-xs text-amber-800 mt-0.5">
                                AI proposes. Human verifies. Repository updates only after approval.
                            </div>
                        </div>
                        <Link to="/data/logo-enrichment/runs">
                            <Button
                                size="sm"
                                className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="open-pending-reviews-btn"
                            >
                                Open Review Queue
                                <ArrowSquareOut size={14} />
                            </Button>
                        </Link>
                    </div>
                )}

                {/* Iter 39 Phase 4 — Pre-filled re-cohort banner */}
                {prefillCohort && (
                    <div
                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 flex items-start gap-3"
                        data-testid="prefill-cohort-banner"
                    >
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-indigo-900">
                                Re-cohort loaded — {(prefillCohort.company_ids?.length || 0).toLocaleString()} compan{(prefillCohort.company_ids?.length || 0) === 1 ? "y" : "ies"} pre-filled from{" "}
                                <Link to={`/data/logo-enrichment/runs/${encodeURIComponent(prefillCohort.source_run_id)}`} className="font-mono text-indigo-700 hover:underline" data-testid="prefill-source-run-link">
                                    {prefillCohort.source_run_id}
                                </Link>
                            </div>
                            <div className="text-xs text-indigo-700 mt-1">
                                Category: <span className="font-mono">{prefillCohort.category}</span>
                                {" · "}
                                Suggested mode: <span className="font-mono uppercase">{prefillCohort.suggested_mode}</span>
                                {" · "}
                                Click <strong>Start</strong> to launch — change mode to Live below if you intend a real write.
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={clearPrefill}
                            className="text-indigo-700 hover:text-indigo-900 inline-flex"
                            title="Discard re-cohort and run the default placeholder cohort instead"
                            data-testid="clear-prefill-btn"
                        >
                            <X size={16} />
                        </button>
                    </div>
                )}
                {/* ── 1. Start Enrichment ─────────────────────────── */}
                <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="start-panel">
                    <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Start enrichment</h2>
                    <div className="flex flex-wrap items-end gap-6">
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-2">Mode</label>
                            <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1" role="tablist">
                                <button
                                    type="button"
                                    data-testid="mode-dry-run"
                                    onClick={() => setMode("dry_run")}
                                    className={`px-4 py-1.5 text-sm rounded font-medium transition-colors ${mode === "dry_run" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                                >
                                    Dry-run
                                </button>
                                <button
                                    type="button"
                                    data-testid="mode-live"
                                    onClick={() => setMode("live")}
                                    className={`px-4 py-1.5 text-sm rounded font-medium transition-colors ${mode === "live" ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                                >
                                    Skip Review Queue
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-2" htmlFor="batch-size">Batch size</label>
                            <Input
                                id="batch-size"
                                data-testid="batch-size-input"
                                type="number"
                                min={1}
                                max={2000}
                                value={batchSize}
                                onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value) || 1))}
                                className="h-9 w-32"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">1–2000 rows</div>
                        </div>
                        <div>
                            <Button
                                data-testid="start-enrichment-btn"
                                onClick={onClickStart}
                                disabled={starting || (run && !isTerminal)}
                                className="gap-2"
                            >
                                <Play size={16} weight="fill" />
                                {starting ? "Starting…" : (mode === "live" ? "Start (Skip Review Queue)" : "Start Dry-run")}
                            </Button>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-4">
                        Concurrency locked to <span className="font-mono">{CONCURRENCY_BY_MODE[mode]}</span> for {mode === "live" ? "Skip-Review-Queue" : "Dry-run"} mode.
                        Rows already attempted in a prior run are skipped automatically.
                        {mode === "live" && (
                            <span className="block mt-1 text-amber-700">
                                Skip-Review-Queue bypasses human verification and writes directly to the Company Repository. Use only with administrator approval.
                            </span>
                        )}
                    </p>
                </section>

                {/* ── 2. Current / Latest Run summary ─────────────── */}
                {run && (
                    <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="run-summary">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <StatusBadge status={run.status} />
                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid="run-mode">
                                        {run.mode === "live" ? "SKIP REVIEW" : "DRY-RUN"}
                                    </span>
                                </div>
                                <div className="font-mono text-sm text-slate-700" data-testid="run-id">{run.run_id}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Started {run.started_at}
                                    {!isTerminal && eta !== null && <> · ETA ~{eta}s</>}
                                    {isTerminal && run.duration_ms > 0 && <> · Took {Math.round(run.duration_ms / 1000)}s</>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={downloadJson}
                                    disabled={!run}
                                    className="gap-2"
                                    data-testid="download-json-btn"
                                >
                                    <DownloadSimple size={14} />
                                    Download JSON
                                </Button>
                            </div>
                        </div>

                        {run.error_message && (
                            <div className="mb-4 p-3 rounded border border-rose-200 bg-rose-50 text-sm text-rose-700" data-testid="run-error-banner">
                                <strong>Run failed:</strong> {run.error_message}
                            </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                            <Tile label="Cohort total" value={run.cohort_total || 0} testId="tile-cohort" />
                            <Tile label="Scanned" value={run.rows_scanned || 0} testId="tile-scanned" />
                            <Tile label="Processed" value={run.rows_processed || 0} accent="text-slate-700" testId="tile-processed" />
                            <Tile label="Domains discovered" value={run.domains_discovered || 0} accent="text-blue-700" testId="tile-domains" />
                            <Tile label={isLive ? "Logos written" : "Logos resolvable"} value={isLive ? (run.rows_wrote_mongo || 0) : (run.logos_acquired || 0)} accent="text-emerald-700" testId="tile-written" />
                            <Tile label="Collisions" value={run.rows_skipped_collision || 0} accent="text-amber-700" testId="tile-collisions" />
                            <Tile label="Errors" value={run.errors_count || 0} accent={run.errors_count > 0 ? "text-rose-700" : undefined} testId="tile-errors" />
                        </div>

                        {/* Iter A 2026-02 — surfaces per-row time-budget overruns
                            so the operator can see if a slow-upstream class
                            existed in this run. Only renders when non-zero
                            so the summary stays calm on healthy runs. */}
                        {(run.rows_timed_out || 0) > 0 && (
                            <div
                                className="mt-3 px-3 py-2 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800 inline-flex items-center gap-2"
                                data-testid="rows-timed-out-banner"
                            >
                                <span className="font-mono tabular-nums">{run.rows_timed_out}</span>
                                <span>row{run.rows_timed_out > 1 ? "s" : ""} hit the 45 s per-row wall-clock budget and were bailed. They remain un-attempted and will be retried on the next run.</span>
                            </div>
                        )}

                        {/* 2026-02 — Third-party portal suppression banner.
                            Surfaces the count + per-category breakdown of
                            rows where discovery would have leaked a news /
                            market-data / brokerage / regulator / parking
                            host. Only renders when non-zero. Operator can
                            click the rose chip in the table to drill in. */}
                        {(run.rows_skipped_third_party_portal || 0) > 0 && (
                            <div
                                className="mt-3 px-3 py-2 rounded border border-rose-200 bg-rose-50 text-xs text-rose-800"
                                data-testid="third-party-banner"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="font-mono tabular-nums">{run.rows_skipped_third_party_portal}</span>
                                    <span>third-party portal candidate{run.rows_skipped_third_party_portal > 1 ? "s" : ""} suppressed (no logo acquired).</span>
                                </div>
                                {run.third_party_breakdown && Object.keys(run.third_party_breakdown).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                                        {Object.entries(run.third_party_breakdown).map(([cat, n]) => (
                                            <span
                                                key={cat}
                                                data-testid={`third-party-chip-${cat}`}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-rose-200 text-rose-700 text-[10px]"
                                            >
                                                <span>{cat.replace(/_/g, " ")}</span>
                                                <span className="font-mono tabular-nums">{n}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Iter B 2026-02 — live in-flight markers.
                            Surfaces the rows currently being processed by
                            the background worker. Refreshed every 5s via
                            the run-document heartbeat patch on the backend
                            and the 2s polling loop on the frontend. */}
                        {!isTerminal && (run.in_flight || []).length > 0 && (
                            <div className="mt-4 rounded border border-blue-200 bg-blue-50 px-3 py-2.5" data-testid="in-flight-panel">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <CircleNotch size={12} weight="bold" className="text-blue-600 animate-spin" />
                                    <span className="text-xs font-medium text-blue-900">
                                        Currently processing ({run.in_flight.length})
                                    </span>
                                    {run.progress_heartbeat_at && (
                                        <span className="text-[10px] text-blue-700/70 font-mono">
                                            heartbeat {new Date(run.progress_heartbeat_at).toLocaleTimeString()}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {run.in_flight.map((it) => (
                                        <span
                                            key={it.company_id}
                                            data-testid={`in-flight-${it.company_id}`}
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white text-blue-800 text-[11px] border border-blue-200"
                                            title={`${it.company_id} · ${it.elapsed_s}s elapsed`}
                                        >
                                            <span className="font-medium">{it.company_name}</span>
                                            <span className="font-mono tabular-nums text-blue-500">{it.elapsed_s}s</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Confidence breakdown — only after the run produces values */}
                        {run.confidence_breakdown && Object.keys(run.confidence_breakdown).length > 0 && (
                            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs" data-testid="confidence-breakdown">
                                <span className="text-slate-500">Confidence:</span>
                                {["high", "medium", "low", "domain-only", "none"].map((k) => (
                                    <span key={k} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-50 border border-slate-200">
                                        <ConfidenceChip value={k} />
                                        <span className="font-mono tabular-nums text-slate-700">{run.confidence_breakdown[k] || 0}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {/* ── 3. Affected Companies ──────────────────────── */}
                {run && (run.sample_outcomes?.length || 0) > 0 && (
                    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="affected-companies">
                        <div className="px-6 py-4 border-b border-slate-200">
                            <h2 className="font-display text-lg font-bold text-slate-900">Affected companies</h2>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Showing {filteredOutcomes.length} of {run.sample_outcomes.length} sample outcome{run.sample_outcomes.length !== 1 ? "s" : ""} from this run (capped at 500). Logo preview pulls directly from the candidate URL the engine resolved — it is the exact image a live run would persist.
                            </p>

                            {/* Filter chips — operator-driven narrowing into the
                                category they want to validate before approving a live run. */}
                            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="outcome-filter-chips">
                                <FilterChip
                                    active={filter === "all"}
                                    label="All"
                                    count={counts.all}
                                    onClick={() => setFilter("all")}
                                    testId="filter-chip-all"
                                />
                                <FilterChip
                                    active={filter === "would_write"}
                                    label="Would write"
                                    count={counts.would_write}
                                    color="bg-emerald-600 text-white border-emerald-600"
                                    onClick={() => setFilter("would_write")}
                                    testId="filter-chip-would-write"
                                />
                                <FilterChip
                                    active={filter === "domain_only"}
                                    label="Domain-only"
                                    count={counts.domain_only}
                                    color="bg-slate-700 text-white border-slate-700"
                                    onClick={() => setFilter("domain_only")}
                                    testId="filter-chip-domain-only"
                                />
                                <FilterChip
                                    active={filter === "collision"}
                                    label="Collisions"
                                    count={counts.collision}
                                    color="bg-amber-500 text-white border-amber-500"
                                    onClick={() => setFilter("collision")}
                                    testId="filter-chip-collisions"
                                />
                                <FilterChip
                                    active={filter === "third_party"}
                                    label="3rd-party portal"
                                    count={counts.third_party}
                                    color="bg-rose-600 text-white border-rose-600"
                                    onClick={() => setFilter("third_party")}
                                    testId="filter-chip-third-party"
                                />
                                <FilterChip
                                    active={filter === "skipped"}
                                    label="Skipped"
                                    count={counts.skipped}
                                    color="bg-slate-500 text-white border-slate-500"
                                    onClick={() => setFilter("skipped")}
                                    testId="filter-chip-skipped"
                                />
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-left text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2.5 font-medium w-14">Preview</th>
                                        <th className="px-4 py-2.5 font-medium">Company</th>
                                        <th className="px-4 py-2.5 font-medium">Identity</th>
                                        <th className="px-4 py-2.5 font-medium">Discovered domain</th>
                                        <th className="px-4 py-2.5 font-medium">Acquisition</th>
                                        <th className="px-4 py-2.5 font-medium">Confidence</th>
                                        <th className="px-4 py-2.5 font-medium" title="V2.0 composite score · hover for breakdown">Score</th>
                                        <th className="px-4 py-2.5 font-medium">Would write?</th>
                                        <th className="px-4 py-2.5 font-medium">Outcome</th>
                                        <th className="px-4 py-2.5 font-medium w-10"></th>
                                    </tr>
                                </thead>
                                <tbody data-testid="affected-rows" className="divide-y divide-slate-100">
                                    {filteredOutcomes.length === 0 && (
                                        <tr>
                                            <td colSpan={10} className="px-4 py-8 text-center text-slate-400" data-testid="affected-rows-empty">
                                                No rows match the “{filter.replace(/_/g, " ")}” filter.
                                            </td>
                                        </tr>
                                    )}
                                    {filteredOutcomes.map((r) => {
                                        const ident = r.nse_symbol || r.bse_code || r.company_id;
                                        // "Would write?" is YES only when the engine resolved a
                                        // logo and the commit-gate predicate would pass. We
                                        // surface the rejection reason as a tooltip so the
                                        // operator never has to expand the row to understand
                                        // why a logo was set aside.
                                        const wouldWrite = !!r.would_write;
                                        const wouldWriteReason = r.skipped_reason
                                            || (r.collision_with ? `collision-with ${r.collision_with}` : "")
                                            || (!r.discovered_domain ? "domain-discovery-failed" : "")
                                            || (!r.candidate_logo_url ? "no-candidate-logo" : "");
                                        return (
                                            <tr key={r.company_id} data-testid={`affected-row-${r.company_id}`} className="hover:bg-slate-50">
                                                <td className="px-4 py-2.5">
                                                    <LogoCandidatePreview url={r.candidate_logo_url} />
                                                </td>
                                                <td className="px-4 py-2.5 text-slate-900">{r.company_name || "—"}</td>
                                                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{ident}</td>
                                                <td className="px-4 py-2.5 font-mono text-xs">
                                                    {r.discovered_domain ? (
                                                        <>
                                                            <a
                                                                href={`https://${r.discovered_domain}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-slate-700 hover:text-blue-600 hover:underline"
                                                                title={`Open https://${r.discovered_domain} in a new tab`}
                                                                data-testid={`discovered-domain-${r.company_id}`}
                                                            >
                                                                {r.discovered_domain}
                                                            </a>
                                                            <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-1.5">
                                                                <span>via {r.domain_source || "?"}</span>
                                                                {r.domain_verified && !r.downgrade_reason && (
                                                                    <span className="text-emerald-700">· verified</span>
                                                                )}
                                                                {r.downgrade_reason && (
                                                                    <span
                                                                        className="inline-block uppercase tracking-wide px-1 py-px rounded bg-amber-100 text-amber-700 text-[9px] font-semibold"
                                                                        title={`Generic-stem downgrade: ${r.downgrade_reason}`}
                                                                        data-testid={`downgrade-badge-${r.company_id}`}
                                                                    >
                                                                        downgraded
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-slate-600">
                                                    {r.logo_source ? (
                                                        <div className="space-y-0.5">
                                                            <div className="font-mono text-slate-700">{r.logo_source}</div>
                                                            {r.candidate_logo_url && (
                                                                <a
                                                                    href={r.candidate_logo_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="font-mono text-[10px] text-blue-600 hover:underline truncate block max-w-[260px]"
                                                                    title={r.candidate_logo_url}
                                                                    data-testid={`candidate-url-${r.company_id}`}
                                                                >
                                                                    {r.candidate_logo_url}
                                                                </a>
                                                            )}
                                                            {r.logo_size > 0 && (
                                                                <div className="text-[10px] text-slate-400 font-mono">
                                                                    {r.logo_ext || "?"} · {r.logo_size.toLocaleString()} B
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5"><ConfidenceChip value={r.confidence} /></td>
                                                <td className="px-4 py-2.5">
                                                    <ScoreCell
                                                        score={r.confidence_score}
                                                        breakdown={r.confidence_score_breakdown}
                                                        companyId={r.company_id}
                                                    />
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    {wouldWrite ? (
                                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`would-write-yes-${r.company_id}`}>
                                                            <CheckCircle size={12} weight="fill" />
                                                            Yes
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500"
                                                            title={wouldWriteReason || "not eligible"}
                                                            data-testid={`would-write-no-${r.company_id}`}
                                                        >
                                                            <XCircle size={12} weight="fill" className="text-slate-400" />
                                                            No
                                                            {wouldWriteReason && (
                                                                <span className="ml-1 text-[10px] text-slate-400 font-mono truncate max-w-[140px]">
                                                                    ({wouldWriteReason})
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5"><OutcomeChip row={r} /></td>
                                                <td className="px-4 py-2.5">
                                                    <Link
                                                        to={`/data/companies/${encodeURIComponent(ident)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-slate-400 hover:text-slate-900 inline-flex"
                                                        title="Open company detail"
                                                        data-testid={`affected-row-link-${r.company_id}`}
                                                    >
                                                        <ArrowSquareOut size={14} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>

            {/* Skip-Review-Queue confirmation */}
            <DestructiveConfirmDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                title="Confirm Skip-Review-Queue enrichment"
                description={`This will discover and persist real logos for up to ${batchSize} placeholder companies — bypassing the human Review Queue. Per-row audit logs are written. Skipped rows can be re-attempted on a later run.`}
                expectedPhrase="SKIP REVIEW QUEUE"
                confirmLabel={`Start (Skip Review Queue) — ${batchSize} rows`}
                confirmingLabel="Queueing…"
                confirming={starting}
                onConfirm={startRun}
                testId="confirm-live-dialog"
            />
        </div>
    );
}
